import React, { createContext, ReactNode, useContext, useState } from "react"

interface HaufeLoadingContextType {
  isHaufeLoading: boolean
  haufeOperationType: string | null
  startHaufeOperation: (operationType: string) => void
  endHaufeOperation: () => void
}

const HaufeLoadingContext = createContext<HaufeLoadingContextType | undefined>(
  undefined
)

// Singleton to allow store actions to trigger Haufe loading states
class HaufeLoadingManager {
  private static instance: HaufeLoadingManager
  private loadingCallbacks: {
    start: (operationType: string) => void
    end: () => void
  } | null = null

  public static getInstance(): HaufeLoadingManager {
    if (!HaufeLoadingManager.instance) {
      HaufeLoadingManager.instance = new HaufeLoadingManager()
    }
    return HaufeLoadingManager.instance
  }

  public setCallbacks(callbacks: {
    start: (operationType: string) => void
    end: () => void
  }) {
    this.loadingCallbacks = callbacks
  }

  public startHaufeOperation(operationType: string) {
    if (this.loadingCallbacks) {
      this.loadingCallbacks.start(operationType)
    }
  }

  public endHaufeOperation() {
    if (this.loadingCallbacks) {
      this.loadingCallbacks.end()
    }
  }
}

export const haufeLoadingManager = HaufeLoadingManager.getInstance()

interface HaufeLoadingProviderProps {
  children: ReactNode
}

export const HaufeLoadingProvider: React.FC<HaufeLoadingProviderProps> = ({
  children,
}) => {
  const [isHaufeLoading, setIsHaufeLoading] = useState(false)
  const [haufeOperationType, setHaufeOperationType] = useState<string | null>(
    null
  )

  const startHaufeOperation = (operationType: string) => {
    setIsHaufeLoading(true)
    setHaufeOperationType(operationType)
  }

  const endHaufeOperation = () => {
    setIsHaufeLoading(false)
    setHaufeOperationType(null)
  }

  // Register callbacks with the singleton
  React.useEffect(() => {
    haufeLoadingManager.setCallbacks({
      start: startHaufeOperation,
      end: endHaufeOperation,
    })

    // Cleanup on unmount
    return () => {
      haufeLoadingManager.setCallbacks({
        start: () => {},
        end: () => {},
      })
    }
  }, [])

  const value = {
    isHaufeLoading,
    haufeOperationType,
    startHaufeOperation,
    endHaufeOperation,
  }

  return (
    <HaufeLoadingContext.Provider value={value}>
      {children}
    </HaufeLoadingContext.Provider>
  )
}

export const useHaufeLoading = () => {
  const context = useContext(HaufeLoadingContext)
  if (context === undefined) {
    throw new Error(
      "useHaufeLoading must be used within a HaufeLoadingProvider"
    )
  }
  return context
}

// Operation types for better UX messaging
export const HAUFE_OPERATIONS = {
  FETCH_LEADS: "Verkaufschancen werden geladen...",
  FETCH_CONTACTS: "Kontakte werden geladen...",
  FETCH_CUSTOMERS: "Kunden werden geladen...",
  FETCH_SALES_QUOTES: "Angebote werden geladen...",
  FETCH_EMPLOYEES: "Mitarbeiter werden geladen...",
  CREATE_CONTACT: "Kontakt wird erstellt...",
  UPDATE_CONTACT: "Kontakt wird aktualisiert...",
  CREATE_CUSTOMER: "Kunde wird erstellt...",
  UPDATE_CUSTOMER: "Kunde wird aktualisiert...",
  CREATE_SALES_QUOTE: "Angebot wird erstellt...",
  UPDATE_SALES_QUOTE: "Angebot wird aktualisiert...",
  FETCH_SALES_QUOTE_DETAILS: "Angebotsdetails werden geladen...",
} as const
